/**
 * Per-product production requirements, surfaced on the Product Master Data
 * grid: an at-a-glance micro-bar of the eight weeks in the cell, and a popover
 * with the week-by-week numbers.
 *
 * Master data is week-invariant while production requirements are per week, so
 * the two never appeared together — you had to switch tabs and step through
 * eight week buttons to answer "does this product actually get made, and
 * when?". This puts that answer on the row.
 *
 * ── Form (see the dataviz method) ────────────────────────────────────────────
 * One series (quantity) across eight ordered categories (weeks), and the exact
 * numbers matter as much as the comparison. So the popover is **meter rows** —
 * label │ bar │ value — not a column chart: a table's value column is
 * legitimate where eight direct labels on a chart would not be. One series
 * means no legend; the popover title names it.
 *
 * The fill is `TOKENS.ganttProduction`, the colour this product already uses
 * for production bars on the Gantt — colour follows the entity, and it passes
 * the palette checks (lightness band, chroma floor, 3:1 against white) where
 * `brandTeal` fails both the band and the chroma floor for a data fill.
 *
 * ── One scale, everywhere ────────────────────────────────────────────────────
 * Both the cell micro-bars and the popover meters are scaled to the **global**
 * maximum across every product and week, never to the row's own peak.
 * Per-row normalisation is the tempting version and it lies: a product making
 * 10 units would draw an identical full-height bar to one making 10,000.
 */

import { memo, useMemo, useState } from 'react'
import { Box, Popover } from '@mui/material'
import { FONT_MONO, FONT_SANS, TOKENS } from './SchedulerDesign'

export const WEEK_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8] as const

/** productCode -> quantity per week, index 0 = week 1. */
export type RequirementsByProduct = Map<string, number[]>

const fmt = (n: number) =>
  n === 0 ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 0 })

/** Bar width as a percentage of the shared scale, with a visible floor so a
 *  small-but-real quantity never renders as nothing at all. */
const widthPct = (value: number, max: number) =>
  value <= 0 ? 0 : Math.max(3, (value / max) * 100)

export const ProductRequirementsCell = memo(
  ({
    productCode,
    productName,
    weeks,
    globalMax,
    isLoading,
  }: {
    productCode: string
    productName?: string
    weeks: number[]
    globalMax: number
    isLoading: boolean
  }) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null)
    const total = useMemo(() => weeks.reduce((a, b) => a + b, 0), [weeks])

    if (isLoading) {
      return (
        <Box sx={{ fontSize: 10.5, color: TOKENS.ink300, fontFamily: FONT_SANS }}>
          loading…
        </Box>
      )
    }

    return (
      <>
        <Box
          component="button"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            setAnchor(e.currentTarget)
          }
          title={
            total > 0
              ? `${fmt(total)} across 8 weeks — click for the weekly breakdown`
              : 'No production requirement in any week'
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            border: 'none',
            background: 'none',
            p: 0,
            cursor: 'pointer',
            fontFamily: FONT_SANS,
            textAlign: 'left',
          }}
        >
          {/* Total first — it is the number being scanned down the column. */}
          <Box
            sx={{
              fontFamily: FONT_MONO,
              fontSize: 11,
              fontWeight: total > 0 ? 700 : 400,
              color: total > 0 ? TOKENS.ink900 : TOKENS.ink300,
              minWidth: 52,
              textAlign: 'right',
              flexShrink: 0,
            }}
          >
            {fmt(total)}
          </Box>
          {/* Eight micro-bars: the demand SHAPE across the horizon, readable
              without opening anything. 2px gaps, per the mark spec. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '2px',
              height: 16,
              flexShrink: 0,
            }}
          >
            {weeks.map((q, i) => (
              <Box
                key={i}
                sx={{
                  width: 4,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-end',
                  // The empty track keeps every week in the same place, so a
                  // gap reads as "no demand that week" rather than as a
                  // missing bar.
                  bgcolor: TOKENS.line100,
                  borderRadius: '1px',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: `${widthPct(q, globalMax)}%`,
                    bgcolor: TOKENS.ganttProduction,
                    borderRadius: '1px 1px 0 0',
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        <Popover
          open={Boolean(anchor)}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                p: 1.5,
                borderRadius: '3px',
                border: `1px solid ${TOKENS.line300}`,
                boxShadow: '0 4px 16px rgba(14,27,34,0.12)',
                minWidth: 280,
              },
            },
          }}
        >
          <Box sx={{ fontFamily: FONT_SANS }}>
            {/* One series, so the title names it instead of a legend. */}
            <Box sx={{ fontSize: 11.5, fontWeight: 700, color: TOKENS.ink900 }}>
              {productCode}
            </Box>
            {productName && (
              <Box sx={{ fontSize: 10.5, color: TOKENS.ink500, mb: 1 }}>
                {productName}
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 0.75,
                mb: 1,
                pb: 1,
                borderBottom: `1px solid ${TOKENS.line200}`,
              }}
            >
              <Box
                sx={{
                  // Sans, NOT the mono face: equal-width digits on a large
                  // standalone figure read gappy and wrong. Mono is kept for
                  // the aligned value columns below and in the grid cell,
                  // where comparing digits down a column is the whole point.
                  fontFamily: FONT_SANS,
                  fontSize: 18,
                  fontWeight: 700,
                  color: TOKENS.ink900,
                  letterSpacing: '-0.01em',
                }}
              >
                {fmt(total)}
              </Box>
              <Box sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>
                total production requirement, weeks 1–8
              </Box>
            </Box>

            {total === 0 ? (
              <Box sx={{ fontSize: 10.5, color: TOKENS.ink500 }}>
                This product has no requirement in any week, so the scheduler
                will not plan it.
              </Box>
            ) : (
              weeks.map((q, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: '2px',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: 10,
                      color: TOKENS.ink400,
                      width: 34,
                      flexShrink: 0,
                    }}
                  >
                    Wk {WEEK_NUMBERS[i]}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      height: 8,
                      bgcolor: TOKENS.line100,
                      borderRadius: '2px',
                      overflow: 'hidden',
                      minWidth: 60,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${widthPct(q, globalMax)}%`,
                        height: '100%',
                        bgcolor: TOKENS.ganttProduction,
                        // Rounded data-end, anchored to the baseline.
                        borderRadius: '0 2px 2px 0',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      fontFamily: FONT_MONO,
                      fontSize: 10.5,
                      color: q > 0 ? TOKENS.ink900 : TOKENS.ink300,
                      width: 58,
                      textAlign: 'right',
                      flexShrink: 0,
                    }}
                  >
                    {fmt(q)}
                  </Box>
                </Box>
              ))
            )}
            <Box
              sx={{
                fontSize: 9.5,
                color: TOKENS.ink300,
                mt: 1,
                pt: 0.75,
                borderTop: `1px solid ${TOKENS.line200}`,
              }}
            >
              Bars share one scale across every product, so lengths are
              comparable between rows.
            </Box>
          </Box>
        </Popover>
      </>
    )
  }
)
ProductRequirementsCell.displayName = 'ProductRequirementsCell'
